import { ReactElement, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import defaultMapStyleImage from 'assets/images/default-map-style.png'
import satelliteMapStyleImage from 'assets/images/satellite-map-style.png'
import darkMapStyleImage from 'assets/images/dark-map-style.png'
import Floating from 'components/Floating'
import { DARK_MAP_STYLE_ID, DEFAULT_MAP_STYLE_ID } from 'config/constants'
import { AngleDownIcon } from 'components/Icons'
import { MapIcon } from 'components/Icons/MapIcon'

const MapStylesList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const MapStyleItem = styled.li`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const MapStyleImage = styled.img`
  width: 40px;
  height: 40px;
`

const MapStyleLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #28265d;
`

const messages = defineMessages({
  mapStyleDefault: {
    id: 'mapStyle.default',
    defaultMessage: 'Default'
  },
  mapStyleSatellite: {
    id: 'mapStyle.satellite',
    defaultMessage: 'Satellite'
  },
  mapStyleDark: {
    id: 'mapStyle.dark',
    defaultMessage: 'Grayscale'
  }
})

interface ISelectMapStylePanelProps {
  setMapStyle: (mapStyle: { mapId: string; mapType: string }) => void
}

export const SelectMapStylePanel = ({ setMapStyle }: ISelectMapStylePanelProps): ReactElement => {
  const mapStylesList = useMemo(() => {
    return [
      {
        label: messages.mapStyleDark,
        mapStyleId: DARK_MAP_STYLE_ID,
        mapType: google.maps.MapTypeId.ROADMAP,
        image: darkMapStyleImage
      },
      {
        label: messages.mapStyleDefault,
        mapStyleId: DEFAULT_MAP_STYLE_ID,
        mapType: google.maps.MapTypeId.ROADMAP,
        image: defaultMapStyleImage
      },
      {
        label: messages.mapStyleSatellite,
        mapStyleId: DEFAULT_MAP_STYLE_ID,
        mapType: google.maps.MapTypeId.HYBRID,
        image: satelliteMapStyleImage
      }
    ]
  }, [])
  const intl = useIntl()
  const [isShowingMapStyles, setIsShowingMapStyles] = useState(false)

  const toggleMapStyles = (): void => {
    setIsShowingMapStyles(!isShowingMapStyles)
  }

  return (
    <Floating.Toolbar hasSingleItem>
      <Floating.ToolbarItem>
        <Floating.ToolbarButton onClick={toggleMapStyles}>
          <MapIcon />
          <Floating.ToolbarButtonAngleIcon isCollapsed={!isShowingMapStyles}>
            <AngleDownIcon />
          </Floating.ToolbarButtonAngleIcon>
        </Floating.ToolbarButton>
      </Floating.ToolbarItem>
      {isShowingMapStyles && (
        <MapStylesList>
          {mapStylesList.map(({ label, mapStyleId, mapType, image }, index) => (
            <MapStyleItem key={index} onClick={(): void => setMapStyle({ mapId: mapStyleId, mapType })}>
              <MapStyleImage src={image} />
              <MapStyleLabel>{intl.formatMessage(label)}</MapStyleLabel>
            </MapStyleItem>
          ))}
        </MapStylesList>
      )}
    </Floating.Toolbar>
  )
}
